import type { ProfitDocType, ProfitDocument } from '@erp/shared';
import { ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button as ShButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/shadcn/index.js';
import { Badge } from '../../../components/ui/index.js';
import { api } from '../../../lib/api.js';
import { formatDate, formatNumber } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

/** Which document to cost. The four shapes the profit engine can answer for. */
export interface SaleRef {
  docType: ProfitDocType;
  docId: string;
}

export interface ProfitDocumentState {
  doc: ProfitDocument | null;
  /** A real failure worth telling the reader about — never a 404 or a 403. */
  error: boolean;
  /**
   * The server has nothing costable behind this document: a draft invoice, a
   * parked sale, a line-less order. Not a failure — a reason to show nothing.
   */
  missing: boolean;
}

/**
 * One sale's costing, fetched only when asked for.
 *
 * Pass `null` to stay idle: that is how a caller without `profits.read` keeps
 * the request from ever leaving the browser, and how a summary block avoids
 * paying for a document nobody has opened yet. A 404 (nothing to cost) and a
 * 403 (a permission this caller was never meant to exercise) are both reported
 * as `missing` rather than as errors, so a caller can render nothing without
 * having to read status codes itself.
 */
export function useProfitDocument(sale: SaleRef | null): ProfitDocumentState {
  const [state, setState] = useState<ProfitDocumentState>({
    doc: null, error: false, missing: false,
  });
  // Depend on the two strings, not the object: callers build the ref inline, so
  // an identity dependency would refetch on every render of theirs.
  const docType = sale?.docType ?? null;
  const docId = sale?.docId ?? null;

  useEffect(() => {
    // Idle, and deliberately NOT cleared: a dialog closing keeps its figures on
    // screen through the close animation instead of blanking to a skeleton for
    // the last 200ms. The next real ref clears them itself, below.
    if (!docType || !docId) return;
    let cancelled = false;
    setState({ doc: null, error: false, missing: false });
    void api()
      .get<ProfitDocument>(`/reports/profit/doc/${docType}/${docId}`)
      .then((r) => {
        if (!cancelled) setState({ doc: r.data, error: false, missing: false });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } }).response?.status;
        const missing = status === 404 || status === 403;
        setState({ doc: null, error: !missing, missing });
      });
    return () => { cancelled = true; };
  }, [docType, docId]);

  return state;
}

export interface SaleCostingDialogProps {
  sale: SaleRef | null;
  onClose: () => void;
  /**
   * A costing the caller already has. Given one, the dialog does not fetch —
   * a page showing the summary figures has paid for these lines already.
   */
  preloaded?: ProfitDocument | null;
}

/**
 * One sale, line by line — the answer to "where did that profit come from?".
 *
 * Fetched on open rather than carried in the report: the row is a summary and
 * this is the receipt behind it, and nobody should pay for the lines of 50
 * sales to read one.
 */
export function SaleCostingDialog({ sale, onClose, preloaded }: SaleCostingDialogProps): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const locale = i18n.language;
  // Idle whenever the caller handed the costing over — see `preloaded`.
  const fetched = useProfitDocument(preloaded ? null : sale);
  const doc = preloaded ?? fetched.doc;
  const failed = fetched.error || fetched.missing;

  const money = (amount: string, currency: string, approximate = false): string =>
    formatMoney(amount, { locale, currency, approximate });
  const qty = (n: string): string =>
    formatNumber(n, { locale }, { maximumFractionDigits: 4 });

  // The rates that actually did work — '1' means the cost was already in the
  // sale's currency and saying so would only add noise.
  const fxRates = useMemo(() => {
    const seen = new Set<string>();
    for (const l of doc?.lines ?? []) {
      if (l.fxRate && Number(l.fxRate) !== 1) seen.add(l.fxRate);
    }
    return [...seen];
  }, [doc]);

  return (
    <Dialog open={sale !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {doc ? doc.docNumber : t('profit.doc.title')}
          </DialogTitle>
          {doc && (
            <p className="text-sm text-fg-muted">
              {formatDate(doc.docDate, { locale })} · {t(`profit.channel.${doc.channel}`)}
            </p>
          )}
        </DialogHeader>

        {failed ? (
          <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
            {t('profit.doc.loadFailed')}
          </p>
        ) : !doc ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure label={t('profit.kpi.revenue')} value={money(doc.revenue, doc.currencyCode)} />
              <Figure label={t('profit.kpi.cost')} value={money(doc.cost, doc.currencyCode)} />
              <Figure
                label={t('profit.kpi.profit')}
                value={money(doc.profit, doc.currencyCode)}
                className={Number(doc.profit) < 0 ? 'text-danger' : 'text-success'}
              />
              <Figure
                label={t('profit.kpi.margin')}
                value={doc.marginPercent === null ? '—' : percent(doc.marginPercent, locale)}
              />
            </dl>

            {doc.excludedLineCount > 0 && (
              <p className="text-xs text-warning">
                {t('profit.doc.excludedNote', {
                  n: formatNumber(doc.excludedLineCount, { locale }),
                })}
              </p>
            )}

            <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('profit.doc.columns.product')}</TableHead>
                    <TableHead className="text-end">{t('profit.doc.columns.quantity')}</TableHead>
                    <TableHead className="text-end">{t('profit.doc.columns.revenue')}</TableHead>
                    <TableHead className="text-end">{t('profit.doc.columns.unitCost')}</TableHead>
                    <TableHead className="text-end">{t('profit.doc.columns.cost')}</TableHead>
                    <TableHead className="text-end">{t('profit.doc.columns.profit')}</TableHead>
                    <TableHead>{t('profit.doc.columns.note')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {doc.lines.map((line) => (
                    <TableRow key={line.lineNo}>
                      <TableCell>
                        <div className="min-w-0 max-w-xs truncate">
                          {line.itemName ?? line.description ?? '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-end font-mono text-xs tabular-nums">
                        <div>
                          {qty(line.quantity)}
                          {line.unitName ? ` ${line.unitName}` : ''}
                        </div>
                        {/* A carton of 840 leaves 840 pieces on the shelf — the cost
                            is per piece, so the piece count has to be visible. */}
                        {line.unitFactor !== '1' && (
                          <div className="text-fg-muted">
                            {t('profit.doc.baseQty', { qty: qty(line.baseQuantity) })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-end font-mono tabular-nums">
                        {money(line.revenueNet, line.currencyCode)}
                      </TableCell>
                      <TableCell className="text-end font-mono text-xs tabular-nums">
                        {line.unitCost === null ? '—' : (
                          <span title={line.isEstimate ? t('profit.estimateRow') : undefined}>
                            {money(
                              line.unitCost,
                              line.costCurrencyCode ?? line.currencyCode,
                              line.isEstimate,
                            )}
                          </span>
                        )}
                        {line.costSource && (
                          <div className="text-fg-muted">
                            {t(`profit.doc.source.${line.costSource}`)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-end font-mono tabular-nums">
                        {line.costTotal === null ? '—' : money(line.costTotal, line.currencyCode)}
                      </TableCell>
                      <TableCell className="text-end font-mono font-semibold tabular-nums">
                        {line.profit === null ? '—' : (
                          <span className={Number(line.profit) < 0 ? 'text-danger' : 'text-success'}>
                            {money(line.profit, line.currencyCode)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {line.costStatus !== 'ok' && (
                          <Badge tone="warning">
                            {t(`profit.excluded.reason.${line.costStatus}`)}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {fxRates.length > 0 && (
              <p className="text-xs text-fg-muted">
                {t('profit.doc.fxNote', {
                  rate: fxRates
                    .map((r) => formatNumber(r, { locale }, { maximumFractionDigits: 6 }))
                    .join(' · '),
                })}
              </p>
            )}

            <DialogFooter>
              <Link to={docPath(doc.docType, doc.docId)}>
                <ShButton variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4" />
                  {t('profit.doc.openDocument')}
                </ShButton>
              </Link>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FigureProps {
  label: string;
  value: string;
  className?: string;
}

function Figure({ label, value, className }: FigureProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className={`font-mono text-lg font-semibold tabular-nums ${className ?? ''}`}>{value}</dd>
    </div>
  );
}

/**
 * Where a row's document opens. A register sale has no page of its own — the
 * receipt is the only per-sale view in the app that a link can reach — and a
 * credit note is a row in `invoices`, so it shares the invoice page.
 */
export function docPath(docType: ProfitDocType, docId: string): string {
  switch (docType) {
    case 'pos_sale': return `/pos/sales/${docId}/print`;
    case 'sales_order': return `/sales/orders/${docId}`;
    case 'invoice':
    case 'credit_note':
    default: return `/sales/invoices/${docId}`;
  }
}

export function percent(value: number, locale: string): string {
  return `${formatNumber(value, { locale }, { maximumFractionDigits: 1 })}%`;
}
